export default function FerpaCompliance() {
  const ferpaRequirements = [
    {
      title: "Educational Record Protection",
      icon: "📁",
      description: "FERPA protects the privacy of student education records and gives parents certain rights.",
      compliance: [
        "All student academic records are classified as educational records",
        "Access is limited to authorized educational personnel only",
        "Records are maintained with appropriate security measures",
        "Students over 18 gain control of their own records"
      ]
    },
    {
      title: "Consent Requirements",
      icon: "✍️", 
      description: "Written consent is generally required before disclosing personally identifiable information.",
      compliance: [
        "Parent/student consent obtained before sharing records with third parties",
        "Clear explanation provided about what information will be shared",
        "Consent forms specify the purpose and parties involved",
        "Consent can be withdrawn at any time"
      ]
    },
    {
      title: "Directory Information",
      icon: "📋",
      description: "Schools may disclose directory information without consent if proper notice is given.",
      compliance: [
        "Limited to basic information like name and grade level",
        "Parents/students can opt-out of directory information disclosure",
        "Annual notification provided about directory information policies",
        "No sensitive academic or behavioral information included"
      ]
    },
    {
      title: "Access Rights",
      icon: "👁️",
      description: "Parents and eligible students have the right to inspect and review education records.",
      compliance: [
        "Records available for review within 45 days of request",
        "Explanation provided for any codes or abbreviations",
        "Copies provided when necessary for access",
        "Right to challenge inaccurate or misleading information"
      ]
    }
  ];

  const examlockCompliance = [
    {
      category: "Data Collection",
      icon: "📊",
      measures: [
        "Collect only the minimum student information necessary for educational purposes",
        "Student names limited to first name and last initial only",
        "No collection of sensitive personal information (SSN, address, etc.)",
        "Clear policies on what data is collected and why"
      ]
    },
    {
      category: "Access Control",
      icon: "🔐",
      measures: [
        "Teachers can only access records of their own students",
        "Role-based access controls prevent unauthorized viewing",
        "Audit logs track all access to student records",
        "Multi-factor authentication available for enhanced security"
      ]
    },
    {
      category: "Data Sharing",
      icon: "🤝",
      measures: [
        "No sharing of student data with advertising companies",
        "No sale of student information to third parties",
        "Service providers bound by strict data protection agreements",
        "Parents/students can request data sharing restrictions"
      ]
    },
    {
      category: "Record Maintenance",
      icon: "🗃️",
      measures: [
        "Secure storage with encryption and backup systems",
        "Regular data accuracy reviews and correction processes",
        "Automatic deletion of records according to retention policies",
        "Export capabilities for record portability"
      ]
    }
  ];

  const studentRights = [
    {
      right: "Right to Inspect Records",
      description: "Students over 18 and parents of minors can review all educational records",
      howToExercise: "Contact your teacher or our support team to request record access",
      timeline: "Records will be made available within 45 days of request"
    },
    {
      right: "Right to Amend Records", 
      description: "Challenge information believed to be inaccurate or misleading",
      howToExercise: "Submit a written request explaining the desired change and reasoning",
      timeline: "Schools must decide within 30 days and provide written response"
    },
    {
      right: "Right to Control Disclosure",
      description: "Consent required before sharing information with most third parties",
      howToExercise: "Review and approve any data sharing requests before they occur",
      timeline: "Consent requests processed immediately upon submission"
    },
    {
      right: "Right to File Complaints",
      description: "Report FERPA violations to the U.S. Department of Education",
      howToExercise: "File complaint with Family Policy Compliance Office (FPCO)",
      timeline: "Complaints must be filed within 180 days of violation"
    }
  ];

  const schoolGuidance = [
    {
      title: "Setting Up FERPA Compliance",
      steps: [
        "Review your school's FERPA policies with administration",
        "Ensure teacher training on educational record privacy",
        "Establish clear procedures for parent consent",
        "Document all data sharing agreements and purposes"
      ]
    },
    {
      title: "Using ExamLock Compliantly",
      steps: [
        "Configure student accounts with minimal information",
        "Review data sharing settings in your account dashboard", 
        "Provide required FERPA notices to parents/students",
        "Monitor and audit student data access regularly"
      ]
    },
    {
      title: "Managing Parent Requests",
      steps: [
        "Respond to record access requests promptly",
        "Provide clear explanations of student performance data",
        "Process amendment requests according to school policy",
        "Document all interactions and decisions made"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-800 to-purple-800 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">🏫 FERPA-Aligned Privacy Practices</h1>
            <p className="text-xl text-blue-100 mb-6 leading-relaxed">
              ExamLock is designed with FERPA principles in mind — protecting student data, limiting access,
              and never selling student information. We are in beta and can sign a school Data Processing Agreement (DPA) on request.
            </p>
            <div className="bg-white bg-opacity-20 rounded-lg px-6 py-4 inline-block">
              <div className="font-bold">Designed with student privacy in mind</div>
            </div>
          </div>
        </div>
      </div>

      {/* FERPA Overview */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-blue-800 mb-6 flex items-center">
                <span className="mr-3">📚</span>
                What is FERPA?
              </h2>
              <div className="text-blue-700 space-y-4">
                <p>
                  The Family Educational Rights and Privacy Act (FERPA) is a federal law that protects 
                  the privacy of student education records. It applies to all schools that receive 
                  federal funding and gives parents and students important rights.
                </p>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-2xl mb-2">👨‍👩‍👧‍👦</div>
                    <h3 className="font-bold text-blue-800 mb-2">Parent Rights</h3>
                    <p className="text-sm text-blue-600">
                      Parents control access to their child's records until the student turns 18
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-2xl mb-2">🎓</div>
                    <h3 className="font-bold text-blue-800 mb-2">Student Rights</h3>
                    <p className="text-sm text-blue-600">
                      Students 18+ gain full control over their educational records
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-2xl mb-2">🏛️</div>
                    <h3 className="font-bold text-blue-800 mb-2">School Duties</h3>
                    <p className="text-sm text-blue-600">
                      Schools must protect records and provide access to authorized individuals
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FERPA Requirements */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">📋 Key FERPA Requirements</h2>
              <p className="text-xl text-gray-600">
                Understanding the core components of FERPA compliance
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {ferpaRequirements.map((requirement, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="flex items-center mb-6">
                    <span className="text-3xl mr-4">{requirement.icon}</span>
                    <h3 className="text-xl font-bold text-gray-900">{requirement.title}</h3>
                  </div>
                  
                  <p className="text-gray-700 mb-6">{requirement.description}</p>
                  
                  <div>
                    <h4 className="font-bold text-gray-800 mb-4">Compliance Requirements:</h4>
                    <ul className="space-y-3">
                      {requirement.compliance.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start text-gray-700">
                          <span className="text-green-500 mr-3 mt-0.5">✅</span>
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ExamLock Compliance Measures */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">🛡️ How ExamLock Approaches Privacy</h2>
              <p className="text-xl text-gray-600">
                Our approach to student data protection — designed with FERPA principles in mind
              </p>
            </div>

            <div className="space-y-8">
              {examlockCompliance.map((category, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-center mb-6">
                    <span className="text-3xl mr-4">{category.icon}</span>
                    <h3 className="text-2xl font-bold text-gray-900">{category.category}</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {category.measures.map((measure, measureIndex) => (
                      <div key={measureIndex} className="bg-white rounded-lg p-4">
                        <div className="flex items-start">
                          <span className="text-blue-500 mr-3 mt-0.5">•</span>
                          <span className="text-gray-700 text-sm">{measure}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Student and Parent Rights */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">⚖️ Your FERPA Rights</h2>
              <p className="text-xl text-gray-600">
                Understanding your rights under FERPA and how to exercise them
              </p>
            </div>

            <div className="grid gap-8">
              {studentRights.map((right, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{right.right}</h3>
                  <p className="text-gray-700 mb-6">{right.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-bold text-blue-800 mb-2">How to Exercise This Right:</h4>
                      <p className="text-blue-700 text-sm">{right.howToExercise}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-bold text-green-800 mb-2">Response Timeline:</h4>
                      <p className="text-green-700 text-sm">{right.timeline}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* School Guidance */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">🎯 Guidance for Schools</h2>
              <p className="text-xl text-gray-600">
                Best practices for maintaining FERPA compliance with ExamLock
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {schoolGuidance.map((guide, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{guide.title}</h3>
                  <div className="space-y-4">
                    {guide.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-start">
                        <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">
                          {stepIndex + 1}
                        </div>
                        <p className="text-gray-700 text-sm">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact and Support */}
      <div className="py-16 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">📞 FERPA Support & Resources</h2>
            <p className="text-xl text-green-100 mb-8 leading-relaxed">
              Need help with FERPA compliance or have questions about student privacy rights? 
              Our specialized team is here to assist.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">🏫</div>
                <div className="font-bold text-lg mb-2">For Schools</div>
                <p className="text-green-100 text-sm">
                  FERPA compliance guidance, training materials, and implementation support
                </p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">👨‍👩‍👧‍👦</div>
                <div className="font-bold text-lg mb-2">For Parents/Students</div>
                <p className="text-green-100 text-sm">
                  Help exercising your FERPA rights and understanding privacy protections
                </p>
              </div>
            </div>

            <div className="bg-white bg-opacity-20 rounded-2xl p-8 mb-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-lg mb-2">ExamLock Privacy Team</h3>
                  <p className="text-green-100 text-sm mb-2">privacy@examlock.ai</p>
                  <p className="text-green-100 text-xs">Response within 24 hours</p>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">U.S. Department of Education</h3>
                  <p className="text-green-100 text-sm mb-2">FPCO@ed.gov</p>
                  <p className="text-green-100 text-xs">For filing FERPA complaints</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <a
                href="/contact"
                className="inline-block bg-white text-green-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-green-50 transition duration-200 shadow-lg mx-2"
              >
                📧 Contact Privacy Team
              </a>
              <a
                href="https://www2.ed.gov/policy/gen/guid/fpco/ferpa/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-green-600 transition duration-200 mx-2"
              >
                🏛️ Official FERPA Information
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}